import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useMessageModal } from '../../contexts/MessageModalContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { sendContact } from '../../utils/sendEmail';
import content from '../../i18n/content';
import './MessageModal.css';

export default function MessageModal() {
    const { isOpen, closeModal } = useMessageModal();
    const { lang } = useLanguage();
    const m = content[lang].modal;

    const [status, setStatus] = useState('idle');
    const [errorMsg, setErrorMsg] = useState('');
    const [fromName, setFromName] = useState('');
    const [messageContent, setMessageContent] = useState('');
    const [website, setWebsite] = useState('');
    const [errors, setErrors] = useState({ fromName: '', messageContent: '' });
    const [touched, setTouched] = useState({ fromName: false, messageContent: false });

    // Lock scroll while open
    useEffect(() => {
        if (!isOpen) return;
        const prevBody = document.body.style.overflow;
        const prevHtml = document.documentElement.style.overflow;
        document.body.style.overflow = 'hidden';
        document.documentElement.style.overflow = 'hidden';
        const block = (e) => { e.preventDefault(); e.stopPropagation(); };
        document.addEventListener('wheel', block, { passive: false });
        document.addEventListener('touchmove', block, { passive: false });
        return () => {
            document.body.style.overflow = prevBody;
            document.documentElement.style.overflow = prevHtml;
            document.removeEventListener('wheel', block);
            document.removeEventListener('touchmove', block);
        };
    }, [isOpen]);

    // Escape key closes modal
    useEffect(() => {
        if (!isOpen) return;
        const onKey = (e) => { if (e.key === 'Escape' && status !== 'sending') handleClose(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, status]);

    const handleClose = () => {
        closeModal();
        setStatus('idle');
        setErrorMsg('');
        setFromName('');
        setMessageContent('');
        setWebsite('');
        setErrors({ fromName: '', messageContent: '' });
        setTouched({ fromName: false, messageContent: false });
    };

    const validateField = (name, value) => {
        if (!value.trim()) {
            if (name === 'fromName') return m.nameRequired;
            if (name === 'messageContent') return m.messageRequired;
        }
        return '';
    };

    const handleBlur = (fieldName) => {
        const value = fieldName === 'fromName' ? fromName : messageContent;
        setTouched(prev => ({ ...prev, [fieldName]: true }));
        setErrors(prev => ({ ...prev, [fieldName]: validateField(fieldName, value) }));
    };

    const handleChange = (fieldName, value) => {
        if (fieldName === 'fromName') setFromName(value);
        else if (fieldName === 'messageContent') setMessageContent(value);
        if (touched[fieldName]) {
            setErrors(prev => ({ ...prev, [fieldName]: validateField(fieldName, value) }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        setTouched({ fromName: true, messageContent: true });
        const nameErr = validateField('fromName', fromName);
        const msgErr = validateField('messageContent', messageContent);
        setErrors({ fromName: nameErr, messageContent: msgErr });
        if (nameErr || msgErr) {
            setStatus('error');
            setErrorMsg(m.genericError);
            return;
        }
        setStatus('sending');
        setErrorMsg('');
        try {
            const result = await sendContact(fromName, messageContent, website);
            if (result.success) {
                setStatus('success');
                setFromName('');
                setMessageContent('');
                setWebsite('');
            } else {
                setStatus('error');
                setErrorMsg(result.error || m.sendFailed);
            }
        } catch (err) {
            console.error('Failed to send contact:', err);
            setStatus('error');
            setErrorMsg(m.sendFailed);
        }
    };

    if (!isOpen) return null;

    return createPortal(
        <div
            className="message-modal-backdrop"
            role="dialog"
            aria-modal="true"
            aria-labelledby={status === 'success' ? 'modal-success-title' : 'modal-form-title'}
            onClick={handleClose}
        >
            <div className="message-modal" onClick={(e) => e.stopPropagation()}>
                {status === 'success' ? (
                    <div className="message-modal-success">
                        <div className="message-modal-success-content">
                            <h3 id="modal-success-title" className="message-modal-success-title">
                                {m.thankYou}
                            </h3>
                            <p className="message-modal-success-message">{m.successMessage}</p>
                        </div>
                        <div className="message-modal-actions message-modal-actions-center">
                            <button type="button" className="message-modal-primary" onClick={handleClose}>
                                {m.gotIt}
                            </button>
                        </div>
                    </div>
                ) : (
                    <>
                        <div className="message-modal-header">
                            <h3 id="modal-form-title" className="message-modal-title">{m.title}</h3>
                            <button
                                type="button"
                                className="message-modal-close"
                                onClick={handleClose}
                                aria-label={m.close}
                                disabled={status === 'sending'}
                            >
                                ×
                            </button>
                        </div>
                        <form className="message-modal-form" onSubmit={handleSubmit} noValidate>
                            <input
                                type="text"
                                name="website"
                                value={website}
                                onChange={(e) => setWebsite(e.target.value)}
                                style={{ display: 'none' }}
                                autoComplete="off"
                                tabIndex="-1"
                                aria-hidden="true"
                            />
                            <label className="message-modal-label">
                                {m.nameLabel}
                                <input
                                    type="text"
                                    className={`message-modal-input${touched.fromName && errors.fromName ? ' message-modal-input-error' : ''}`}
                                    placeholder={m.namePlaceholder}
                                    value={fromName}
                                    onChange={(e) => handleChange('fromName', e.target.value)}
                                    onBlur={() => handleBlur('fromName')}
                                    disabled={status === 'sending'}
                                    required
                                />
                                {touched.fromName && errors.fromName && (
                                    <span className="message-modal-help-text">{errors.fromName}</span>
                                )}
                            </label>
                            <label className="message-modal-label">
                                {m.messageLabel}
                                <textarea
                                    className={`message-modal-textarea${touched.messageContent && errors.messageContent ? ' message-modal-textarea-error' : ''}`}
                                    placeholder={m.messagePlaceholder}
                                    value={messageContent}
                                    onChange={(e) => handleChange('messageContent', e.target.value)}
                                    onBlur={() => handleBlur('messageContent')}
                                    rows={6}
                                    disabled={status === 'sending'}
                                    required
                                />
                                {touched.messageContent && errors.messageContent && (
                                    <span className="message-modal-help-text">{errors.messageContent}</span>
                                )}
                            </label>
                            {status === 'error' && errorMsg && (
                                <div className="message-modal-error" role="alert">{errorMsg}</div>
                            )}
                            <div className="message-modal-actions">
                                <button
                                    type="button"
                                    className="message-modal-secondary"
                                    onClick={handleClose}
                                    disabled={status === 'sending'}
                                >
                                    {m.cancel}
                                </button>
                                <button
                                    type="submit"
                                    className="message-modal-primary"
                                    disabled={status === 'sending'}
                                >
                                    {status === 'sending' ? m.sending : m.send}
                                </button>
                            </div>
                        </form>
                    </>
                )}
            </div>
        </div>,
        document.body
    );
}
